import "~/css/style.css";

import moonSvg from "~/img/svg/moon.svg";
import sunSvg from "~/img/svg/sun.svg";
import { getReportScheduleModal, getSourcesStatus, getUserReportModal } from "~/js/api.js";
import { isMobile, isTablet } from "~/js/helpers";
import { Popover } from "~/js/utils/popover";

let dialogModulePromise;
const loadDialogModule = () => {
	if (!dialogModulePromise) {
		dialogModulePromise = import("~/js/utils/dialog.js");
	}
	return dialogModulePromise;
};

async function initDialogSourcesStatus() {
	const openButton = document.querySelector("#dSSOpenButton");
	if (!openButton) return;

	let dialog;
	openButton.addEventListener("click", async () => {
		if (!dialog) {
			const { Dialog } = await loadDialogModule();
			dialog = new Dialog({
				classes: "min-h-[650px]",
				id: "sourceStatusDialog",
				open: async (dialog) => {
					getSourcesStatus().then((data) => {
						setTimeout(() => {
							dialog.content(data);
						}, 200);
					});
				},
			});
		}
		dialog.open();
	});
}

async function initDialogMenu() {
	const openButton = document.querySelector("#openMenu");
	if (!openButton) return;
	const closeButton = document.querySelector("#closeMenu");
	if (!closeButton) return;

	const menu = document.querySelector("#dialogMenu");

	const menuItems = menu.querySelectorAll(".menu-animate:not(#closeMenu)");
	menuItems.forEach((item, i) => {
		item.style.setProperty("--stagger", `${50 + i * 15}ms`);
	});
	closeButton.style.setProperty("--stagger", `${50 + menuItems.length * 15}ms`);

	openButton.addEventListener("click", () => menu.showModal());
	closeButton.addEventListener("click", () => menu.close());

	menu.addEventListener("click", (e) => {
		const rect = menu.getBoundingClientRect();
		const isInDialog =
			rect.top <= e.clientY &&
			e.clientY <= rect.top + rect.height &&
			rect.left <= e.clientX &&
			e.clientX <= rect.left + rect.width;
		if (!isInDialog) {
			menu.close();
		}
	});
}

async function initDialogUserReport() {
	const openButtons = document.querySelectorAll("[data-dialog='user-report']");
	if (!openButtons.length) return;

	for (const openButton of openButtons) {
		const reportId = openButton.dataset.id;
		let dialog;

		openButton.addEventListener("click", async () => {
			if (!dialog) {
				const { UserReportDialog } = await loadDialogModule();
				dialog = new UserReportDialog({
					classes: "max-w-[1300px] min-h-[90svh] lg:min-h-[800px]",
					reportId,
					open: async (dialog) => {
						getUserReportModal(reportId)
							.then((data) => {
								setTimeout(() => {
									dialog.content(data);
								}, 300);
							})
							.catch(() => {
								dialog.content(
									`<div class="flex flex-col items-center justify-center py-12 text-center">
										<p class="text-slate-600 dark:text-slate-400">${gettext("Impossibile caricare il report. Riprova più tardi.")}</p>
									</div>`,
								);
							});
					},
				});
				dialog.success((data) => {
					if (data && data.action === "delete") {
						window.location.reload();
					}
				});
			}
			dialog.open();
		});
	}
}

// Syncs the lime schedule indicator on report rows after the schedule dialog resolves.
function syncScheduleIndicators(data) {
	if (!data?.userReportId || typeof data.hasSchedules !== "boolean") return;

	if (!data.hasSchedules && new URLSearchParams(window.location.search).get("scheduled") === "1") {
		// The index can be filtered to scheduled reports only: removing the
		// last schedule drops the row, which needs a server render.
		window.location.reload();
		return;
	}

	const id = CSS.escape(data.userReportId);
	const hosts = document.querySelectorAll(`[data-schedule-dot-host][data-id='${id}']`);
	for (const host of hosts) {
		const indicator = host.querySelector("[data-schedule-indicator]");
		if (data.hasSchedules && !indicator) {
			const dot = document.createElement("span");
			dot.setAttribute("data-schedule-indicator", "");
			dot.className =
				"absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-foreground bg-lime-500";
			dot.setAttribute("aria-hidden", "true");
			const label = document.createElement("span");
			label.className = "sr-only";
			label.textContent = gettext("Programmazione presente");
			host.append(dot, label);
		} else if (!data.hasSchedules && indicator) {
			if (indicator.nextElementSibling?.classList.contains("sr-only")) {
				indicator.nextElementSibling.remove();
			}
			indicator.remove();
		}
	}
}

async function initDialogScheduleReport() {
	const openButtons = document.querySelectorAll("[data-dialog='schedule-report']");
	if (!openButtons.length) return;

	for (const openButton of openButtons) {
		const reportId = openButton.dataset.reportId || openButton.dataset.id || null;
		const scheduleId = openButton.dataset.scheduleId || null;
		let dialog;
		let openingPromise;

		openButton.addEventListener("click", async () => {
			if (openingPromise) return;

			openingPromise = (async () => {
				if (!dialog) {
					const { ScheduleReportDialog } = await loadDialogModule();
					dialog = new ScheduleReportDialog({
						classes: "max-w-[850px] min-h-[500px]",
						reportId,
						scheduleId,
						open: async (dialog) => {
							getReportScheduleModal(reportId, scheduleId)
								.then((data) => {
									setTimeout(() => {
										dialog.content(data);
									}, 200);
								})
								.catch(() => {
									dialog.content(
										`<div class="flex flex-col items-center justify-center py-12 text-center">
											<p class="text-slate-600 dark:text-slate-400">${gettext("Impossibile caricare la programmazione. Riprova più tardi.")}</p>
										</div>`,
									);
								});
						},
					});
					dialog.success((data) => {
						syncScheduleIndicators(data);
						// No await: awaiting close() here re-marks the dialog as
						// fulfilled and swallows the next resolve.
						dialog.close(1200);
					});
				}
				await dialog.open();
			})();

			try {
				await openingPromise;
			} finally {
				openingPromise = null;
			}
		});
	}
}

function initLanguageDropdown() {
	const langDropdownBtn = document.querySelector("#language-dropdown");
	const languageDropdownTemplate = document.querySelector("#language-dropdown-template");
	if (!langDropdownBtn || !languageDropdownTemplate) return;

	const langDropdown = new Popover(langDropdownBtn, {
		trigger: "click",
		placement: "bottom-center",
	});
	langDropdown.setContent(languageDropdownTemplate);

	const langDropdownButtons = document.querySelectorAll("button[data-language]");
	if (!langDropdownButtons) return;

	for (const button of langDropdownButtons) {
		button.addEventListener("click", () => {
			const formData = new FormData();
			formData.append("language", button.dataset.language);

			fetch(window._galada.urls.setLanguage, {
				method: "POST",
				headers: {
					"X-CSRFToken": window._galada.csrfToken,
				},
				body: formData,
			}).then((response) => {
				if (response.ok) {
					window.location.reload(response.url);
				}
			});
		});
	}
}

function initSpinner() {
	const spinner = document.querySelector("#spinner-template");
	if (!spinner) return;

	window._galada.spinner = {
		defaultMessage: gettext("Caricamento in corso"),
		element: spinner,
		message: spinner.querySelector("span[data-message]"),
		show: (message = "") => {
			window._galada.spinner.message.textContent = message || window._galada.spinner.defaultMessage;
			spinner.classList.remove("hidden");
		},
		hide: () => {
			spinner.classList.add("hidden");
		},
	};
}

function initSwitchTheme() {
	const switchThemeBtn = document.querySelector("#switch-theme");
	if (!switchThemeBtn) return;

	const switchThemeIcon = (theme) => {
		return `<img src="${
			theme === "light" ? moonSvg : sunSvg
		}" class="pointer-events-none" alt="Switch theme" />`;
	};

	const toggleTheme = async () => {
		const theme = localStorage.getItem("theme");
		const newTheme = theme === "light" ? "dark" : "light";
		window.dispatchEvent(new CustomEvent("themeChange", { detail: { newTheme: newTheme } }));

		document.documentElement.classList.remove(theme);
		document.documentElement.classList.add(newTheme);
		switchThemeBtn.innerHTML = switchThemeIcon(newTheme);
		localStorage.setItem("theme", newTheme);
	};

	const theme = localStorage.getItem("theme");
	if (!theme) {
		const prefersDarkScheme = window.matchMedia("(prefers-color-scheme: dark)");
		localStorage.setItem("theme", prefersDarkScheme.matches ? "dark" : "light");
	}
	switchThemeBtn.innerHTML = switchThemeIcon(theme);
	switchThemeBtn.addEventListener("click", async () => {
		if (
			!document.startViewTransition ||
			window.matchMedia("(prefers-reduced-motion: reduce)").matches
		) {
			toggleTheme();
			return;
		}

		await document.startViewTransition(() => {
			toggleTheme();
		}).ready;
	});
}

function initUserDropdown() {
	const userDropdownBtn = document.querySelector("#user-dropdown");
	const userDropdownTemplate = document.querySelector("#user-dropdown-template");
	if (userDropdownBtn && userDropdownTemplate) {
		const userDropdown = new Popover(userDropdownBtn, {
			trigger: "click",
			placement: "bottom-end",
		});
		userDropdown.setContent(userDropdownTemplate);
	}
}

document.addEventListener("DOMContentLoaded", async () => {
	initDialogMenu();
	initDialogUserReport();
	initDialogScheduleReport();
	initDialogSourcesStatus();
	initLanguageDropdown();
	initSwitchTheme();
	initSpinner();
	initUserDropdown();

	window._galada = window._galada || {};
	window._galada.isMobile = isMobile();
	window._galada.isTablet = isTablet();
});
